import { isApolloError } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { onKeyDown } from '@openx/components-form/onKeyDown.ts';
import { type CreateSegment, uploadSegmentFormFields } from '@openx/types/segments.ts';
import { type ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useSegmentContext } from '../../utils/context/SegmentContext.tsx';
import { dataChunkSize } from './constants.ts';
import {
  type DataType,
  type UploadFileProgressProps,
  type UploadFileSuccessSummaryProps,
  uploadFileSummaryStatus,
} from './types.ts';
import { UploadFileFormFields } from './UploadFileFormFields.tsx';
import { UploadFileProgress } from './UploadFileProgress.tsx';
import { UploadFileSummary } from './UploadFileSummary.tsx';
import { prepareDataToUpload } from './utils/prepareDataToUpload.ts';
import { getValidationSchema } from './validationSchema.ts';

type UploadFormData = {
  name: string;
  dataType: DataType;
  file: File;
};

export interface UploadFormExposedMethods {
  submitForm: () => void;
}

type Props = {
  setCancelLabel: (label: string) => void;
  setDialogLoading: (loading: boolean) => void;
  toogleConfirmationDisabled: (disabled: boolean) => void;
  toogleConfirmationHidden: (hidden: boolean) => void;
};

export const UploadFileDialogContent = forwardRef(
  (
    { setCancelLabel, setDialogLoading, toogleConfirmationDisabled, toogleConfirmationHidden }: Props,
    ref: ForwardedRef<UploadFormExposedMethods>
  ) => {
    const { t } = useTranslation();
    const { useUploadSegment } = useSegmentContext();

    const { addUsersToSegment, setStatusToPublishing, uploadSegment } = useUploadSegment();

    const [uploadFileProgress, setUploadFileProgress] = useState<UploadFileProgressProps>();
    const [uploadFileSummary, setUploadFileSummary] = useState<UploadFileSuccessSummaryProps>();

    const formMethods = useForm<UploadFormData>({
      mode: 'onChange',
      resolver: yupResolver(getValidationSchema(t), {}, { rawValues: true }),
    });

    useImperativeHandle(ref, () => {
      return {
        submitForm() {
          formMethods.handleSubmit(onSubmit)();
        },
      };
    });

    const { setError: setFormFieldError } = formMethods;
    const onSubmit = useCallback(
      // biome-ignore lint/suspicious/useAwait: biome2 update
      async (formValues: UploadFormData): Promise<unknown> => {
        let createdSegment: CreateSegment | undefined;

        return new Promise<void>((resolve, reject) => {
          setDialogLoading(true);
          const file = formValues[uploadSegmentFormFields.FILE];
          const dataType = formValues[uploadSegmentFormFields.DATA_TYPE];
          const showProgress = file.size > dataChunkSize;
          let totalRecordsProcessed = 0;
          let totalRecordsFailed = 0;
          let progress = 0;

          if (showProgress) {
            setUploadFileProgress({
              progress,
              recordsProcessed: totalRecordsProcessed,
            });
          }

          prepareDataToUpload({
            chunkSize: dataChunkSize,
            dataType,
            file,
            onComplete: () => {
              if (!createdSegment) {
                throw new Error(t('No valid records found, try changing the data type or uploading another file.'));
              }

              setStatusToPublishing(createdSegment.id);

              setUploadFileSummary({
                dataType: dataType,
                recordsFailed: totalRecordsFailed,
                recordsProcessed: totalRecordsProcessed,
                status: uploadFileSummaryStatus.SUCCESS,
              });

              resolve();
            },
            onError: error => {
              if (createdSegment) {
                setFormFieldError(uploadSegmentFormFields.FILE, {
                  message: t('Error uploading the file. Please try again'),
                });

                setUploadFileProgress(undefined);
                setUploadFileSummary(undefined);
              }

              reject(error);
            },
            onProcessedDataChunk: async (recordsProcessed: number, dataToUpload: string[][]): Promise<void> => {
              totalRecordsProcessed += recordsProcessed;
              totalRecordsFailed += recordsProcessed - dataToUpload.length;
              progress += Math.round(((progress + dataChunkSize) / file.size) * 100);

              if (!dataToUpload.length) return;

              if (!createdSegment) {
                createdSegment = await uploadSegment(formValues[uploadSegmentFormFields.NAME]);
              }

              createdSegment &&
                (await addUsersToSegment(createdSegment.id, dataToUpload, [
                  formValues[uploadSegmentFormFields.DATA_TYPE],
                ]));

              if (showProgress) {
                setUploadFileProgress({
                  progress,
                  recordsProcessed: totalRecordsProcessed,
                });
              }
            },
          });
        })
          .then(() => {
            setCancelLabel(t('Close'));
            toogleConfirmationHidden(true);
          })
          .catch(error => {
            if (!isApolloError(error) && error instanceof Error) {
              setFormFieldError(uploadSegmentFormFields.FILE, {
                message: error.message,
              });
            }

            setUploadFileProgress(undefined);
          })
          .finally(() => setDialogLoading(false));
      },
      [
        t,
        addUsersToSegment,
        uploadSegment,
        setCancelLabel,
        setDialogLoading,
        toogleConfirmationHidden,
        setStatusToPublishing,
        setFormFieldError,
      ]
    );

    const { isValid } = formMethods.formState;
    useEffect(() => {
      toogleConfirmationDisabled(!isValid);
    }, [toogleConfirmationDisabled, isValid]);

    return (
      <Box
        data-test="confirmation-dialog-content"
        sx={theme => ({
          borderTop: `solid 1px ${theme.palette.divider}`,
          padding: theme.spacing(3, 2, 0, 2),
        })}
      >
        <Box data-test="upload-file-form">
          <FormProvider {...formMethods}>
            <form onKeyDown={onKeyDown}>
              {!uploadFileProgress && !uploadFileSummary && (
                <Box
                  data-test="confirmation-dialog-content"
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                  }}
                >
                  <UploadFileFormFields />
                </Box>
              )}
            </form>
          </FormProvider>

          {uploadFileProgress && !uploadFileSummary && <UploadFileProgress {...uploadFileProgress} />}

          {uploadFileSummary && <UploadFileSummary {...uploadFileSummary} />}
        </Box>
      </Box>
    );
  }
);
